import { useMemo, useState } from 'react';
import Breadcrumb from '../ui/Breadcrumb';
import AccountPasswordForm from '../ui/AccountPasswordForm';
import { IconDroplet, IconUsers } from '../ui/icons';
import { SettingItem, SettingsSectionTitle, Toggle } from '../ui/SettingsControls';
import { backgroundPresetsForTheme } from '../ui/panelAppearance';

function Swatches({ themeMode, value, onPick, disabled }) {
  const list = backgroundPresetsForTheme(themeMode);
  return (
    <div className="SettingsSwatches">
      {list.map((p) => {
        const active = String(value || '') === String(p.key);
        return (
          <button
            key={p.key}
            type="button"
            className={`SettingsSwatch ${active ? 'is-active' : ''}`}
            style={{ background: p.preview }}
            onClick={() => onPick?.(p.key)}
            disabled={disabled}
            title={p.label}
            aria-label={p.label}
          />
        );
      })}
    </div>
  );
}

export default function AccountSettingsPage({ user, onUserUpdated, panelSettings, onSaveSettings }) {
  const canEdit = Boolean(panelSettings?.editable);
  const loading = panelSettings?.status === 'loading';
  const saveError = String(panelSettings?.error || '').trim();
  const settings = panelSettings?.settings && typeof panelSettings.settings === 'object' ? panelSettings.settings : null;
  const [localSaving, setLocalSaving] = useState(false);

  const appearance = useMemo(() => {
    const defaults = {
      theme_mode: 'dark',
      font: 'space',
      background_preset_dark: 'midnight',
      background_preset_light: 'lavender',
      mobile_header_sticky: false,
    };
    return { ...defaults, ...((settings && settings.appearance) || {}) };
  }, [settings]);

  async function patchAppearance(patch) {
    if (!onSaveSettings) return;
    if (!canEdit) return;
    const next = { ...(settings || {}), appearance: { ...appearance, ...(patch || {}) } };
    setLocalSaving(true);
    try {
      await onSaveSettings(next);
    } finally {
      setLocalSaving(false);
    }
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Paramètres du compte' }]} />
        </div>
      </header>

      {saveError ? (
        <div className="Error" role="alert" style={{ marginBottom: 12 }}>
          {saveError}
        </div>
      ) : null}

      {settings ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="CardHeader">
            <div className="TitleMd">
              <span className="ButtonInline" style={{ gap: 10 }}>
                <IconDroplet /> Apparence du panel
              </span>
            </div>
          </div>

          {loading ? <div className="Muted">Chargement…</div> : null}

          <div style={{ display: 'grid', gap: 12 }}>
            <SettingsSectionTitle style={{ marginTop: 0 }}>Apparence</SettingsSectionTitle>

            <SettingItem
              label="Mode"
              right={
                <select
                  className="Select"
                  value={String(appearance.theme_mode || 'dark')}
                  onChange={(e) => patchAppearance({ theme_mode: e.target.value })}
                  disabled={!canEdit || localSaving}
                >
                  <option value="dark">Mode sombre</option>
                  <option value="light">Mode clair</option>
                </select>
              }
            />

            <SettingItem
              label="Police"
              right={
                <select
                  className="Select"
                  value={String(appearance.font || 'space')}
                  onChange={(e) => patchAppearance({ font: e.target.value })}
                  disabled={!canEdit || localSaving}
                >
                  <option value="space">Space Grotesk</option>
                  <option value="inter">Inter</option>
                </select>
              }
            />

            <SettingItem
              label="Fond du panel"
              right={
                <Swatches
                  themeMode={String(appearance.theme_mode || 'dark')}
                  value={String(appearance.theme_mode || 'dark') === 'light' ? appearance.background_preset_light : appearance.background_preset_dark}
                  onPick={(k) => {
                    const mode = String(appearance.theme_mode || 'dark');
                    patchAppearance(mode === 'light' ? { background_preset_light: k } : { background_preset_dark: k });
                  }}
                  disabled={!canEdit || localSaving}
                />
              }
            />

            <SettingItem
              label="Header fixe (mobile)"
              right={
                <Toggle
                  checked={Boolean(appearance.mobile_header_sticky)}
                  onChange={(v) => patchAppearance({ mobile_header_sticky: v })}
                  disabled={!canEdit || localSaving}
                  ariaLabel="Header fixe (mobile)"
                />
              }
            >
              Garde l’en-tête du panel visible en haut sur mobile.
            </SettingItem>
          </div>
        </div>
      ) : (
        <div className="Hint" style={{ marginBottom: 14 }}>
          Sélectionne un site pour modifier l’apparence du panel.
        </div>
      )}

      <div className="Card">
        <div className="CardHeader">
          <div className="TitleMd">
            <span className="ButtonInline" style={{ gap: 10 }}>
              <IconUsers /> Changer le mot de passe
            </span>
          </div>
        </div>

        <AccountPasswordForm user={user} mustChange={false} onUserUpdated={onUserUpdated} />
      </div>
    </div>
  );
}
